import React, { MouseEvent, useEffect } from 'react';
import { PreventSwipeNavigation } from '@noths/polaris-client-components';
import { Media } from '@noths/polaris-client-responsive';
import { getProductImageURL, PrimaryLabel } from '@noths/polaris-client-ribbons-design-system';
import { Breakpoint } from '@noths/polaris-client-styles';
import { isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import Image from 'next/image';

import { Product } from 'src/types';
import { getIconPath } from 'src/utils';
import {
  PRODUCT_IMAGES_TAB_ID_PREFIX,
  PRODUCT_IMAGES_TABPANEL_CONTAINER_ID,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
  PROGRESS_INDICATOR_TEST_ID,
} from '../constants';
import { ThumbnailsContainer } from '../containers/ThumbnailsContainer';
import { useProductImagesCarousel } from '../hooks/useProductImagesCarousel';
import {
  ImagesLocation,
  NextOrPreviousPayload,
  OpenModalPayload,
  ProductImagesState,
  SetCurrentImagePayload,
  ThumbnailOrientation,
} from '../types';
import * as styles from '../styles/ProductImages.styles';

const ICON_ARROW = getIconPath('icons-24-arrow-right.svg');

const handleImageClick = (
  e: MouseEvent<HTMLAnchorElement>,
  onOpenModal: MultipleProductImagesProps['onOpenModal'],
) => {
  e.preventDefault();
  e.stopPropagation();
  onOpenModal({ location: 'Main image' });
};

const handleNavigationButtonClick = (
  e: MouseEvent<HTMLButtonElement>,
  navigationAction: VoidFunction,
) => {
  e.preventDefault();
  e.stopPropagation();
  navigationAction();
};

export interface MultipleProductImagesProps {
  product: Product;
  currentImage: ProductImagesState['currentImage'];
  navigatedBy: ProductImagesState['navigatedBy'];
  onOpenModal: (payload: OpenModalPayload) => void;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
}

export const MultipleProductImages = ({
  product: { images, isNew, onSale, salePercentage, purchasable, available, availability },
  currentImage,
  navigatedBy,
  setCurrentImage,
  onOpenModal,
  onNextImage,
  onPrevImage,
}: MultipleProductImagesProps) => {
  const [imageFocus, setImageFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);
  const location: ImagesLocation = 'Main image';

  useEffect(() => {
    if (currentImage > 0) {
      // User has navigated past the first image
      setHasInteractedWithCarousel(true);
    }
  }, [currentImage]);

  const [carouselContainerRef, carouselItemRefs] = useProductImagesCarousel(
    (entry) => {
      const imageIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentImage != imageIndex) {
        setCurrentImage({ imageIndex, navigationType: 'swipe', location: 'Main image' });
      }
    },
    currentImage,
    navigatedBy,
    images.length,
  );

  return (
    <PreventSwipeNavigation css={styles.imagesWrapper} aria-live="polite">
      <Media greaterThanOrEqual={Breakpoint.LARGE} css={styles.fresnelThumbnails}>
        <ThumbnailsContainer
          location={location}
          orientation={ThumbnailOrientation.VERTICAL}
          onThumbnailClick={(imageIndex) =>
            setCurrentImage({
              imageIndex: imageIndex,
              navigationType: 'click',
              location,
            })
          }
        />
      </Media>
      <div css={styles.carouselContainer}>
        <div
          ref={carouselContainerRef}
          css={styles.mainImageWrapperWithThumbnails}
          id={PRODUCT_IMAGES_TABPANEL_CONTAINER_ID}
          data-image-focus={imageFocus}
        >
          {images.map(({ href: src, title }, i) => (
            <div
              role="tabpanel"
              aria-labelledby={`${PRODUCT_IMAGES_TAB_ID_PREFIX}${i}`}
              aria-hidden={currentImage !== i}
              id={`${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
              data-item-index={i}
              css={styles.carouselSlide}
              ref={(node) => (carouselItemRefs.current[i] = node as HTMLElement)}
              key={src}
            >
              <a
                href={getProductImageURL(src, 'original')}
                css={styles.mainImageLink}
                tabIndex={currentImage === i ? undefined : -1}
                onClick={(e) => handleImageClick(e, onOpenModal)}
                onFocus={(e) => setImageFocus(isKeyboardTriggeredEvent(e))}
                onBlur={() => setImageFocus(false)}
              >
                <Image
                  src={getProductImageURL(
                    src,
                    i === 0 || hasInteractedWithCarousel ? 'original' : 'normal',
                  )}
                  alt={`${title}, ${i + 1} of ${images.length}`}
                  css={styles.mainImage}
                  layout="fill"
                  priority={i === 0}
                  unoptimized
                />
              </a>
            </div>
          ))}
          <PrimaryLabel
            className="product-image__label"
            css={styles.productLabel}
            isNew={isNew}
            onSale={onSale}
            salePercentage={salePercentage}
            unavailable={!available && availability === 'suspended'}
            hasStock={purchasable}
          />
        </div>
        <Media greaterThanOrEqual={Breakpoint.MEDIUM}>
          <button
            onClick={(e) => handleNavigationButtonClick(e, () => onPrevImage({ location }))}
            css={[styles.carouselNavigationButton, styles.prevNavigationButton]}
            aria-label="Show previous image"
            aria-controls={PRODUCT_IMAGES_TABPANEL_CONTAINER_ID}
          >
            <img src={ICON_ARROW} alt="" width={24} height={24} />
          </button>
          <button
            onClick={(e) => handleNavigationButtonClick(e, () => onNextImage({ location }))}
            css={[styles.carouselNavigationButton, styles.nextNavigationButton]}
            aria-label="Show next image"
            aria-controls={PRODUCT_IMAGES_TABPANEL_CONTAINER_ID}
          >
            <img src={ICON_ARROW} alt="" width={24} height={24} />
          </button>
        </Media>
        <p
          css={styles.progressIndicator}
          aria-hidden="true"
          data-testid={PROGRESS_INDICATOR_TEST_ID}
        >
          {currentImage + 1} of {images.length}
        </p>
      </div>
      <Media at={Breakpoint.MEDIUM} css={styles.fresnelThumbnails}>
        <ThumbnailsContainer
          location={location}
          orientation={ThumbnailOrientation.HORIZONTAL}
          onThumbnailClick={(imageIndex) =>
            setCurrentImage({
              imageIndex: imageIndex,
              navigationType: 'click',
              location,
            })
          }
        />
      </Media>
    </PreventSwipeNavigation>
  );
};
